import { WorkflowGraphEdgeType } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphEdge/types";
import { WorkflowGraphNodeType } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphNode/types";
import {
  useGetOneWorkflowTemplate,
  useListWorkflowTemplates,
} from "#src/components/hooks/adapters/useWorkflowTemplates";
import useLocalization from "#src/hooks/useLocalization";
import { useQuery } from "@tanstack/react-query";
import {
  DatasetAdapter,
  DatasetType,
  FormSchemaAdapter,
  FormSchemaType,
  WorkflowTemplateApiVersion,
  WorkflowTemplateType,
} from "@validereinc/domain";
import { useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { WorkflowGraphParallelNodeType } from "../ParallelGraphNode/types";
import {
  extractDatasetIdsFromWorkflowTemplate,
  extractFormSchemaIdsFromWorkflowTemplate,
  extractWorkflowTemplateIdsFromWorkflowTemplate,
  getLayoutedElements,
  getPathMap,
  getReversePathMap,
  mapInitialVariables,
  mapTaskVariables,
  mapTemplateVariables,
  mapTriggerVariables,
  isWorkflowGraphInitialLoading,
  mergeWorkflowGraphNodePositions,
  shouldClearWorkflowGraphNodesAndEdges,
  workflowTemplateToEdges,
  workflowTemplateToNodes,
} from "./useWorkflowGraph.helpers";
import { WorkflowVisualizationProp } from "./WorkflowGraphCanvas";
import { useWorkflowGraphState } from "./WorkflowGraphStore";

const useWorkflowGraph = (props: WorkflowVisualizationProp) => {
  const { fitView } = useReactFlow();

  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    setOriginalConfiguration,
    setOriginalTriggers,
    setTemplateVariables,
    setTaskVariables,
    setTriggerVariables,
    setInitialVariables,
    setPathMap,
    setReversePathMap,
    currentConfiguration,
    currentTriggers,
    setCurrentConfiguration,
    setCurrentTriggers,
    setWorkflowTemplateApiVersion,
    isInEditMode,
    resetState,
  } = useWorkflowGraphState();

  const { localize } = useLocalization();

  const { templateId } = props;

  const initialSetupVariables = useRef({
    initialNodesAndEdgesSet: false,
    initialFitViewRan: false,
  });

  useEffect(() => {
    resetState();
    initialSetupVariables.current = {
      initialNodesAndEdgesSet: false,
      initialFitViewRan: false,
    };
  }, [templateId, resetState]);

  const {
    data: workflowTemplateResponse,
    isLoading: isLoadingTemplate,
    isFetching: isFetchingTemplate,
  } = useGetOneWorkflowTemplate({ id: templateId }, { enabled: !!templateId });

  useEffect(() => {
    const values = workflowTemplateResponse?.data;
    if (!values || isInEditMode || isFetchingTemplate) return;

    const pathMap = getPathMap(values.config);
    setOriginalConfiguration(values.config);
    setOriginalTriggers(values.triggers);
    setCurrentConfiguration(values.config);
    setCurrentTriggers(values.triggers);
    setWorkflowTemplateApiVersion(
      values.api_version ?? WorkflowTemplateApiVersion.V1
    );
    setTemplateVariables(mapTemplateVariables(values));
    setTaskVariables(mapTaskVariables(values.config));
    setTriggerVariables(mapTriggerVariables(values.triggers));
    setInitialVariables(mapInitialVariables(values));
    setPathMap(pathMap);
    setReversePathMap(getReversePathMap(pathMap));
  }, [
    workflowTemplateResponse?.data,
    templateId,
    isInEditMode,
    isFetchingTemplate,
  ]);

  const formSchemaIdsToFetch = useMemo(
    () =>
      extractFormSchemaIdsFromWorkflowTemplate({
        config: currentConfiguration,
        triggers: currentTriggers,
      }),
    [currentConfiguration, currentTriggers]
  );

  const formSchemasQuery = useQuery({
    queryKey: ["formSchema", { $in: formSchemaIdsToFetch }],
    queryFn: async () =>
      FormSchemaAdapter.getList({
        filters: {
          id: formSchemaIdsToFetch,
        },
        pageSize: 500,
      }),
    enabled: formSchemaIdsToFetch.length > 0,
  });

  const sortedFormSchemasById = useMemo(() => {
    const sorted: Record<string, FormSchemaType> = {};

    (formSchemasQuery.data?.data ?? []).forEach((formSchema) => {
      sorted[formSchema.id] = formSchema;
    });

    return sorted;
  }, [formSchemasQuery.data?.data]);

  const workflowTemplateIdsToFetch = useMemo(
    () =>
      extractWorkflowTemplateIdsFromWorkflowTemplate({
        triggers: currentTriggers,
      }),
    [currentTriggers]
  );

  const datasetIdsToFetch = useMemo(
    () =>
      extractDatasetIdsFromWorkflowTemplate({
        triggers: currentTriggers,
      }),
    [currentTriggers]
  );

  const workflowTemplatesQuery = useListWorkflowTemplates(
    {
      filters: {
        id: workflowTemplateIdsToFetch,
      },
    },
    { enabled: workflowTemplateIdsToFetch.length > 0 }
  );

  const sortedWorkflowTemplatesById = useMemo(() => {
    const sorted: Record<string, WorkflowTemplateType> = {};

    (workflowTemplatesQuery.data?.data ?? []).forEach((workflowTemplate) => {
      sorted[workflowTemplate.id] = workflowTemplate;
    });

    return sorted;
  }, [workflowTemplatesQuery.data?.data]);

  const datasetsQuery = useQuery({
    queryKey: ["dataset", { $in: datasetIdsToFetch }],
    queryFn: async () =>
      DatasetAdapter.getList({
        filters: {
          id: datasetIdsToFetch,
        },
        pageSize: 500,
      }),
    enabled: datasetIdsToFetch.length > 0,
  });

  const sortedDatasetsById = useMemo(() => {
    const sorted: Record<string, DatasetType> = {};

    (datasetsQuery.data?.data ?? []).forEach((dataset) => {
      sorted[dataset.id] = dataset;
    });

    return sorted;
  }, [datasetsQuery.data?.data]);

  const isLoading = isWorkflowGraphInitialLoading({
    templateId,
    isLoadingTemplate,
    hasWorkflowTemplateData: Boolean(workflowTemplateResponse?.data),
    formSchemaIdsToFetch,
    isLoadingFormSchemas: formSchemasQuery.isLoading,
    hasFormSchemasData: Boolean(formSchemasQuery.data),
    workflowTemplateIdsToFetch,
    isLoadingWorkflowTemplates: workflowTemplatesQuery.isLoading,
    hasWorkflowTemplatesData: Boolean(workflowTemplatesQuery.data),
    datasetIdsToFetch,
    isLoadingDatasets: datasetsQuery.isLoading,
    hasDatasetsData: Boolean(datasetsQuery.data),
  });

  const hasStaleGraphNodes =
    nodes.length > 0 && workflowTemplateResponse?.data?.id !== templateId;

  const generatedNodesAndEdges = useMemo(() => {
    if (
      shouldClearWorkflowGraphNodesAndEdges({
        isLoading,
        hasRenderedGraphNodes: nodes.length > 0,
        hasStaleGraphNodes,
      })
    ) {
      return { finalNodes: [], finalEdges: [] };
    }

    const finalNodes = workflowTemplateToNodes({
      template: {
        config: currentConfiguration,
        triggers: currentTriggers,
      },
      formSchemas: sortedFormSchemasById,
      workflowTemplates: sortedWorkflowTemplatesById,
      datasets: sortedDatasetsById,
      localize,
    });
    const finalEdges = workflowTemplateToEdges(
      {
        config: currentConfiguration,
        triggers: currentTriggers,
      },
      {
        formSchemas: sortedFormSchemasById,
      }
    );

    return { finalNodes, finalEdges };
  }, [
    sortedFormSchemasById,
    sortedWorkflowTemplatesById,
    sortedDatasetsById,
    currentConfiguration,
    currentTriggers,
    isLoading,
    isInEditMode,
    nodes.length,
    hasStaleGraphNodes,
  ]);

  const autoLayoutAndUpdateState = useCallback(
    async ({
      nodes,
      edges,
    }: {
      nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>;
      edges: WorkflowGraphEdgeType[];
    }) => {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        await getLayoutedElements(nodes, edges);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    },
    [setNodes, setEdges]
  );

  useEffect(() => {
    const currentNodePositions = new Map(
      nodes.map((node) => [node.id, node.position])
    );

    const nodesWithPreservedPositions = mergeWorkflowGraphNodePositions({
      generatedNodes: generatedNodesAndEdges.finalNodes,
      currentNodePositions,
    });

    autoLayoutAndUpdateState({
      nodes: nodesWithPreservedPositions,
      edges: generatedNodesAndEdges.finalEdges,
    }).then(() => {
      initialSetupVariables.current.initialNodesAndEdgesSet = true;
    });
  }, [JSON.stringify(generatedNodesAndEdges), autoLayoutAndUpdateState]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (
      initialSetupVariables.current.initialNodesAndEdgesSet &&
      !initialSetupVariables.current.initialFitViewRan
    ) {
      timeout = setTimeout(() => {
        window.requestAnimationFrame(() => {
          fitView();
          initialSetupVariables.current.initialFitViewRan = true;
        });
      }, 100);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [JSON.stringify({ nodes, edges })]);

  return {
    autoLayoutAndUpdateState,
    isLoading,
  };
};

export { useWorkflowGraph };
